import React from "react";
import { Loader2, Sparkles, RefreshCw } from "lucide-react";
import { useImageStudio, StudioMode, matchCondition } from "../../context/ImageStudioContext";
import ParamField from "./ParamField";
import ReferenceDropzone from "./ReferenceDropzone";
import PresetGallery from "./PresetGallery";
import PresetForm from "./PresetForm";

const MODE_LABELS: Record<StudioMode, string> = {
  generate: "Generate",
  edit: "Edit",
  control: "Control",
  style: "Style",
  upscale: "Upscale",
};

const ControlsPanel: React.FC = () => {
  const {
    presetMode,
    activePreset,
    presetNeedsMask,
    openPresets,
    exitPresets,
    modes,
    mode,
    toolsForMode,
    toolKey,
    selectTool,
    currentSpec,
    params,
    setParam,
    randomizeSeed,
    referenceKey,
    referencePreviewUrl,
    uploadingReference,
    uploadReference,
    clearReference,
    maskB64,
    styleReferenceKey,
    styleReferencePreviewUrl,
    uploadingStyleReference,
    uploadStyleReference,
    clearStyleReference,
    result,
    variants,
    setVariants,
    generating,
    generate,
    catalogLoading,
  } = useImageStudio();

  const needsImage =
    !!currentSpec &&
    (currentSpec.needs_image ||
      (currentSpec.key === "sd3.5" && params.mode === "image-to-image"));
  // Ultra accepts an optional reference image (image-to-image when provided).
  const optionalImage = !!currentSpec?.optional_image;

  // Some params only apply with/without a reference image. Hide the ones that
  // would silently be ignored so the controls always reflect the real request:
  //   ultra : aspect_ratio is text-to-image only, strength is image-to-image only
  //   sd3.5 : same split, driven by its explicit mode param
  const paramHidden = (name: string): boolean => {
    if (currentSpec?.key === "ultra") {
      if (name === "aspect_ratio") return !!referenceKey;
      if (name === "strength") return !referenceKey;
    }
    if (currentSpec?.key === "sd3.5") {
      const i2i = params.mode === "image-to-image";
      if (name === "aspect_ratio") return i2i;
      if (name === "strength") return !i2i;
    }
    return false;
  };

  const promptParam = currentSpec?.params.find((p) => p.name === "prompt") || null;
  const otherParams = (currentSpec?.params || []).filter(
    (p) => p.name !== "prompt" && !paramHidden(p.name)
  );

  // Preset readiness: every required, visible field is satisfied + mask (if
  // needed). Image fields live in referenceKey/styleReferenceKey, not params.
  const presetReady =
    !!activePreset &&
    activePreset.fields
      .filter((f) => f.required && (!f.show_when || matchCondition(f.show_when, params)))
      .every((f) =>
        f.type === "image"
          ? f.slot === "style"
            ? !!styleReferenceKey
            : !!referenceKey
          : params[f.name] !== undefined && params[f.name] !== ""
      ) &&
    (!presetNeedsMask || !!maskB64);

  // Every required tool param must have a value (e.g. prompt, search_prompt) —
  // otherwise the button would enable and the backend would 400 on click.
  const toolParamsReady = (currentSpec?.params || []).every(
    (p) => !p.required || (params[p.name] !== undefined && params[p.name] !== "")
  );

  // Outpaint must extend in at least one direction — mirror the backend rule
  // here so the button gates instead of the click round-tripping into a 400.
  const outpaintReady =
    currentSpec?.key !== "outpaint" ||
    ["left", "right", "up", "down"].some((d) => Number(params[d]) > 0);

  const toolDisabled =
    generating ||
    catalogLoading ||
    !currentSpec ||
    !toolParamsReady ||
    !outpaintReady ||
    (needsImage && !referenceKey) ||
    (!!currentSpec?.needs_mask && !maskB64) ||
    (!!currentSpec?.needs_style_image && !styleReferenceKey);

  const inPreset = presetMode && !!activePreset;
  const showFooter = inPreset || (!presetMode && !!currentSpec);
  const generateDisabled = inPreset ? generating || !presetReady : toolDisabled;
  // "Regenerate" only when the last result came from this same preset/tool —
  // after switching surfaces the next run is a fresh generation.
  const resultFromHere =
    !!result && (inPreset ? result.tool === activePreset?.key : result.tool === currentSpec?.key);
  const buttonLabel = resultFromHere ? "Regenerate" : "Generate";

  // Variants only make sense where the model output varies — presets and any
  // tool with a seed. Deterministic tools (remove-background, fast-upscale)
  // would produce N near-identical images.
  const variantCapable =
    inPreset || (currentSpec?.params || []).some((p) => p.name === "seed");

  const referenceLabel =
    {
      "style-guide": "Style image",
      "style-transfer": "Content image",
      "control-sketch": "Sketch image",
      "control-structure": "Structure image",
    }[currentSpec?.key || ""] || "Reference image";

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {/* Tabs: Presets + raw tool modes */}
        <div className="flex gap-0.5 rounded-lg bg-surface-2 p-1">
          <button
            type="button"
            onClick={openPresets}
            className={`flex-1 rounded-md px-1 py-1.5 text-xs font-medium transition-colors ${
              presetMode ? "bg-surface-overlay text-primary shadow-sm" : "text-muted hover:text-primary"
            }`}
          >
            Presets
          </button>
          {modes.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => exitPresets(m)}
              className={`flex-1 rounded-md px-1 py-1.5 text-xs font-medium transition-colors ${
                !presetMode && mode === m
                  ? "bg-surface-overlay text-primary shadow-sm"
                  : "text-muted hover:text-primary"
              }`}
            >
              {MODE_LABELS[m]}
            </button>
          ))}
        </div>

        {presetMode ? (
          activePreset ? <PresetForm /> : <PresetGallery />
        ) : (
          <>
            {/* Tool / model picker */}
            {toolsForMode.length > 1 && (
              <div className="space-y-1">
                <label className="block text-xs font-medium text-muted">
                  {mode === "generate" ? "Model" : "Tool"}
                </label>
                <select
                  value={toolKey}
                  onChange={(e) => selectTool(e.target.value)}
                  className="w-full rounded-md bg-surface-2 border border-border-default px-3 py-2 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-violet-500/60"
                >
                  {toolsForMode.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {toolsForMode.length === 1 && currentSpec && (
              <p className="text-xs text-subtle">{currentSpec.label}</p>
            )}

            {(needsImage || optionalImage) && (
              <ReferenceDropzone
                label={needsImage ? referenceLabel : `${referenceLabel} (optional)`}
                previewUrl={referencePreviewUrl}
                uploading={uploadingReference}
                onDrop={uploadReference}
                onClear={clearReference}
              />
            )}

            {/* A loaded input the current model can't consume (e.g. Core is
                text-to-image only) — say so instead of silently ignoring it. */}
            {!needsImage && !optionalImage && referenceKey && mode === "generate" && (
              <p className="rounded-md bg-surface-2 px-2.5 py-2 text-xs text-muted">
                This model is text-to-image only and won't use the loaded image. Switch to
                Stable Image Ultra or SD3.5 to build on it.
              </p>
            )}

            {currentSpec?.needs_style_image && (
              <ReferenceDropzone
                label="Style image"
                previewUrl={styleReferencePreviewUrl}
                uploading={uploadingStyleReference}
                onDrop={uploadStyleReference}
                onClear={clearStyleReference}
              />
            )}

            {currentSpec?.key === "outpaint" && !outpaintReady && (
              <p className="rounded-md bg-surface-2 px-2.5 py-2 text-xs text-muted">
                Set at least one direction (left, right, up or down) above 0 to extend
                the image.
              </p>
            )}

            {currentSpec?.needs_mask && (
              <p className="rounded-md bg-surface-2 px-2.5 py-2 text-xs text-muted">
                {referenceKey
                  ? maskB64
                    ? "Mask ready — adjust controls and Generate."
                    : "Paint the area to change on the canvas →"
                  : "Upload a reference, then paint the area to change."}
              </p>
            )}

            {promptParam && (
              <ParamField
                param={promptParam}
                value={params[promptParam.name]}
                onChange={(v) => setParam(promptParam.name, v)}
              />
            )}

            {otherParams.map((p) => (
              <ParamField
                key={p.name}
                param={p}
                value={params[p.name]}
                onChange={(v) => setParam(p.name, v)}
                onRandomize={p.name === "seed" ? randomizeSeed : undefined}
              />
            ))}
          </>
        )}
      </div>

      {showFooter && (
        <div className="space-y-2.5 border-t border-border-default p-4">
          {variantCapable && (
            <div className="flex items-center justify-between">
              <span
                className="text-xs font-medium text-muted"
                title="Generate several options at once and pick the best"
              >
                Variants
              </span>
              <div className="flex gap-0.5 rounded-md bg-surface-2 p-0.5">
                {[1, 2, 3, 4].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setVariants(n)}
                    className={`h-6 w-7 rounded text-xs font-medium transition-colors ${
                      variants === n
                        ? "bg-surface-overlay text-primary shadow-sm"
                        : "text-muted hover:text-primary"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={generate}
            disabled={generateDisabled}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {generating ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Generating…
              </>
            ) : resultFromHere ? (
              <>
                <RefreshCw size={16} /> {buttonLabel}
              </>
            ) : (
              <>
                <Sparkles size={16} /> {buttonLabel}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default ControlsPanel;
